import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    AircraftRegisterEntryDto,
    AttachmentHandler,
    AuthorityConditionDto,
    AuthorityRegisterSummaryDto,
    AuthorityTypeDto,
    CheckExpiryDto,
    CraftService,
    CreateAircraftRegisterEntryCommand,
    CurrentAttachmentVersionDto,
    DO_NOTHING,
    FlyFreelyError,
    FlyFreelyLoggingService,
    NameId,
    OrganisationAuthorityDto,
    OrganisationAuthorityGroup,
    OrganisationAuthorityService,
    OrganisationService,
    PersonsOrganisationDto,
    Register,
    RpaAuthorityService,
    RpaRegisterExpirySummary,
    SimpleOrganisationDto,
    UiOrganisationAuthorityDto,
    UpdateAircraftRegisterEntryCommand,
    UpdateRegisterSettingsCommand,
    UserService,
    WorkTracker,
    displayableRegisterModeLookup,
    fromLocalDate,
    toLocalDate
} from '@flyfreely-portal-ui/flyfreely';
import { FormatRpaPipe } from '@flyfreely-portal-ui/resource-ui';
import { FormatDatePipe, FormatDateTimePipe } from '@flyfreely-portal-ui/ui';
import { RpaDialoguesService } from 'libs/rpa/src/lib/rpa-dialogues.service';
import moment from 'moment';
import { BsModalRef, ModalOptions } from 'ngx-bootstrap/modal';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import {
    BehaviorSubject,
    Observable,
    ReplaySubject,
    Subject,
    combineLatest,
    firstValueFrom,
    forkJoin,
    of
} from 'rxjs';
import {
    catchError,
    map,
    mergeMap,
    shareReplay,
    switchMap,
    takeUntil,
    tap
} from 'rxjs/operators';
import {
    buildCheckContent,
    getPopoverValue,
    mapCheckExpiry,
    validateExistingEntityEndDate,
    validateExistingEntityStartDate,
    validateNewEntityEndDate,
    validateNewEntityStartDate
} from '../helpers';
import {
    AircraftRegisterEntryWithRpaMaintenance,
    CheckResults,
    EditEntryForm,
    NewEntryForm,
    RegisterCheck,
    RegisterEntry,
    RpaRegisterEntry,
    SelectableStatusSource,
    UiRegisterConditionsHandler,
    mergeCheckResults
} from '../interfaces';

@Component({
    selector: 'rpa-register-dialogue',
    templateUrl: './rpa-register-dialogue.component.html'
})
export class RpaRegisterDialogue implements OnInit, OnDestroy {
    @Input() authority: OrganisationAuthorityDto;
    @Input() managingOrganisationId: number;
    @Input() organisationId: number;
    @Input() authorityType: AuthorityTypeDto;
    @Input() register: AuthorityRegisterSummaryDto;

    organisationAuthority: UiOrganisationAuthorityDto;
    availableAuthorities: NameId<number | null>[];

    availableOrganisations$ = new ReplaySubject<SimpleOrganisationDto[]>();

    /**
     * Organisations the current user can list RPA from (intersection of
     * availableOrganisations and the user's own orgs with CRAFT_LIST permission).
     * Used for the add-entry organisation selector only.
     */
    addCandidateOrganisations$: Observable<SimpleOrganisationDto[]>;

    /**
     * The organisation whose RPA are shown as candidates for adding.
     * Defaults to the current organisationId.
     */
    selectedCandidateOrganisationId: number;

    candidates: NameId[] = [];

    sortList: { [key: string]: any };

    title = 'RPA';
    hasProfilePicture = false;
    checks: RegisterCheck[] = [];
    entries: RpaRegisterEntry[] = [];
    entriesBuf$ = new BehaviorSubject<RpaRegisterEntry[]>([]);

    // the ID of the entry being edited
    editing: number;

    checkResults: CheckResults;
    registerConditionsHandler: UiRegisterConditionsHandler;

    expiry: RpaRegisterExpirySummary;
    mappedExpiries: {
        [entryId: number]: { [checkId: number]: CheckExpiryDto };
    } = {};

    rpaRegister: Register<
        CreateAircraftRegisterEntryCommand,
        UpdateAircraftRegisterEntryCommand,
        AircraftRegisterEntryDto
    >;

    displayableRegisterModeLookup = displayableRegisterModeLookup;

    attachmentHandler: AttachmentHandler;
    attachments: CurrentAttachmentVersionDto[];

    error: string;

    @ViewChild('popover', { static: false })
    popover: PopoverDirective;

    statuses = [
        { name: 'Pending', value: 'PENDING' },
        { name: 'Active', value: 'ACTIVE' },
        { name: 'Suspended', value: 'SUSPENDED' },
        { name: 'Auto', value: 'AUTO' }
    ];

    private workTracker = new WorkTracker();
    working = false;

    private ngUnsubscribe$ = new Subject<void>();

    formGroup: FormGroup<{
        add: FormGroup<NewEntryForm>;
        edit: FormGroup<EditEntryForm>;
    }>;

    expandedSideSheet = false;

    hasManagingOrganisation$: Observable<boolean>;

    constructor(
        public modal: BsModalRef<RpaRegisterDialogue>,
        modalOptions: ModalOptions,
        private craftService: CraftService,
        private formatRpaPipe: FormatRpaPipe,
        private formatDatePipe: FormatDatePipe,
        private formatDateTimePipe: FormatDateTimePipe,
        private organisationAuthorityService: OrganisationAuthorityService,
        private rpaAuthorityService: RpaAuthorityService,
        private organisationService: OrganisationService,
        private rpaDialoguesService: RpaDialoguesService,
        private logging: FlyFreelyLoggingService,
        private commonDialoguesService: CommonDialoguesService,
        private userService: UserService
    ) {
        modalOptions.closeInterceptor = () => {
            if (this.popover != null && this.popover.isOpen) {
                this.popover.hide();
                return Promise.reject();
            }
            return Promise.resolve();
        };
    }

    ngOnInit() {
        this.selectedCandidateOrganisationId = this.organisationId;

        const userOrgsWithCraftList: SimpleOrganisationDto[] = this.userService
            .findUsersOrganisations()
            .filter(o =>
                o.permissions?.includes(
                    PersonsOrganisationDto.Permissions.CRAFT_LIST
                )
            )
            .map(o => ({
                id: o.id,
                name: o.name,
                personalOrganisation: false
            }));

        this.addCandidateOrganisations$ = this.availableOrganisations$.pipe(
            map(available =>
                available.filter(o =>
                    userOrgsWithCraftList.some(u => u.id === o.id)
                )
            ),
            shareReplay(1)
        );

        this.hasManagingOrganisation$ = combineLatest([
            of(this.authority.availableActions.canManage),
            this.availableOrganisations$,
            this.entriesBuf$
        ]).pipe(
            map(
                ([canManage, organisationList, entries]) =>
                    canManage &&
                    (organisationList.length > 1 ||
                        (entries.length > 1 &&
                            entries.some(
                                v =>
                                    v.managingOrganisationId !==
                                    entries[0].managingOrganisationId
                            )))
            ),
            shareReplay(1)
        );

        this.rpaRegister = this.organisationAuthorityService.aircraftRegister(
            this.authority.id,
            this.register.id,
            this.managingOrganisationId
        );

        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));

        this.attachmentHandler = this.rpaAuthorityService.attachmentHandler(
            this.authority.id,
            this.organisationId
        );

        this.attachmentHandler.attachments$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(attachments => (this.attachments = attachments));

        this.formGroup = new FormGroup({
            add: new FormGroup({
                subjectId: new FormControl([], [Validators.required]),
                startDate: new FormControl(undefined, [Validators.required]),
                endDate: new FormControl(undefined),
                statusSource: new FormControl<SelectableStatusSource>('AUTO', [
                    Validators.required
                ])
            }),
            edit: new FormGroup({
                startDate: new FormControl(undefined, [Validators.required]),
                endDate: new FormControl(undefined),
                statusSource: new FormControl(undefined, [Validators.required])
            })
        });

        this.sortList = {};
        this.fetchData();
        this.registerConditionsHandler = this.getConditionsHandler();

        if (this.authority.availableActions.canManage) {
            this.organisationAuthorityService
                .findOrganisationsWithAccess(
                    this.authority.id,
                    this.managingOrganisationId
                )
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe({
                    next: orgs => this.availableOrganisations$.next(orgs),
                    error: error =>
                        this.logging.error(
                            error,
                            'Error fetching available organisations'
                        )
                });
        }
    }

    ngOnDestroy(): void {
        if (this.attachmentHandler != null) {
            this.attachmentHandler.destroy();
        }
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();

        this.availableOrganisations$.complete();
        this.entriesBuf$.complete();
    }

    fetchData() {
        forkJoin([
            this.refreshCandidates(),
            this.refreshExisting(),
            this.refreshExpiries(),
            this.refreshAvailableAuthorities()
        ])
            .pipe(
                switchMap(([candidates, entries, expiries, authorities]) => {
                    this.candidates = candidates;
                    this.entries = entries;
                    this.entriesBuf$.next(entries);
                    this.availableAuthorities = [
                        { id: null, name: 'None' }
                    ].concat(this.parseAuthorities(authorities));
                    this.add.controls.startDate.setValidators([
                        Validators.required,
                        validateNewEntityStartDate(
                            this.entries,
                            this.formGroup,
                            'RPA'
                        )
                    ]);
                    this.add.controls.endDate.setValidators([
                        validateNewEntityEndDate(
                            this.entries,
                            this.formGroup,
                            'RPA'
                        )
                    ]);
                    return this.refreshChecks();
                }),
                switchMap(checks => {
                    this.checks = checks;
                    return this.refreshCheckResults();
                }),
                takeUntil(this.ngUnsubscribe$),
                catchError(error => {
                    this.logging.error(error, 'Error fetching register data');
                    return of({});
                })
            )
            .subscribe({
                next: checkResults => {
                    this.checkResults = checkResults;
                },
                error: error => {
                    this.logging.error(error, 'Error in fetchData');
                }
            })
            .add(this.workTracker.createTracker());
    }

    refreshRegister() {
        this.organisationAuthorityService
            .findAuthority(this.authority.id, this.managingOrganisationId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: authority => {
                    this.organisationAuthority = authority;
                    const register = authority.registers.find(
                        r => r.id === this.register.id
                    );
                    this.register = register ?? this.register;
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error refreshing register details: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    refreshAvailableAuthorities() {
        return this.organisationAuthorityService.findAuthorities(
            this.organisationId,
            this.organisationId
        );
    }

    getConditionsHandler() {
        return {
            findCandidateAuthorityTypes: () =>
                this.rpaAuthorityService.findAvailableAuthorityTypes(
                    this.organisationId
                ),
            findRegisterConditions: () =>
                this.rpaRegister.findRegisterConditions(),
            updateRegisterConditions: (conditions: AuthorityConditionDto[]) =>
                this.rpaRegister.updateRegisterConditions(
                    {
                        managingOrganisationId: this.organisationId,
                        authorityRegisterId: this.register.id,
                        conditions
                    },
                    this.authority.id
                ),
            updateRegisterSettings: (
                registerMode: UpdateRegisterSettingsCommand.RegisterMode
            ) =>
                this.rpaRegister.updateRegisterSettings(
                    {
                        authorityRegisterId: this.register.id,
                        registerMode
                    },
                    this.authority.id
                )
        };
    }

    refreshCheckResults() {
        if (this.entries == null || this.entries.length === 0) {
            return of({});
        }

        return forkJoin(
            this.entries.map(e =>
                this.rpaRegister.findCheckValues(e.id).pipe(
                    map(results => ({
                        status: results.status,
                        entryId: e.id,
                        checks: results.checks.map(r => {
                            if (this.mappedExpiries[e.id] != null) {
                                this.mappedExpiries = {
                                    ...this.mappedExpiries,
                                    [e.id]: {
                                        ...this.mappedExpiries[e.id],
                                        [r.authorityConditionId]:
                                            mapCheckExpiry(
                                                this.expiry,
                                                r,
                                                this.register.id
                                            )
                                    }
                                };
                            } else {
                                this.mappedExpiries = {
                                    ...this.mappedExpiries,
                                    [e.id]: {
                                        [r.authorityConditionId]:
                                            mapCheckExpiry(
                                                this.expiry,
                                                r,
                                                this.register.id
                                            )
                                    }
                                };
                            }
                            return {
                                checkId:
                                    r.authorityConditionId ??
                                    r.verificationEntityId ??
                                    r.entityId ??
                                    -1,
                                checkConditionType: r.conditionType,
                                entityId: e.subjectId,
                                content: buildCheckContent(
                                    r,
                                    date =>
                                        this.formatDatePipe.transform(
                                            moment(date)
                                                .endOf('day')
                                                .toISOString()
                                        ),
                                    timestamp =>
                                        this.formatDateTimePipe.transform(
                                            timestamp
                                        )
                                ),
                                popover: getPopoverValue(r),
                                verificationEntityId: r.verificationEntityId,
                                details: {
                                    acknowledgementTime: r.updatedAt
                                },
                                activity: r
                            };
                        })
                    }))
                )
            )
        ).pipe(
            map(results => results.reduce(mergeCheckResults, {})),
            takeUntil(this.ngUnsubscribe$)
        );
    }

    onCheckUpdated() {
        this.popover?.hide();
        this.refreshExpiries()
            .pipe(
                switchMap(() => this.refreshCheckResults()),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe({
                next: checkResults => {
                    this.checkResults = checkResults;
                },
                error: error => {
                    this.logging.error(error, 'Error updating check results');
                }
            })
            .add(this.workTracker.createTracker());
    }

    changeManagingOrganisation(
        entry: RegisterEntry,
        value: { managingOrganisationId: number }
    ) {
        this.rpaRegister
            .changeRegisterEntryManagingOrganisation(
                entry.id,
                this.authority.id,
                {
                    managingOrganisationId: this.managingOrganisationId,
                    registerEntryManagingOrganisationId:
                        value.managingOrganisationId
                }
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: () => {
                    entry.managingOrganisationId = value.managingOrganisationId;
                    this.logging.success('Managing organisation updated');
                },
                error: error =>
                    this.logging.error(
                        error,
                        'Error updating managing organisation'
                    )
            })
            .add(this.workTracker.createTracker());
    }

    findCheckAttachment(checkName: string) {
        return this.attachments.find(a => a.name === checkName) ?? null;
    }

    refreshExpiries() {
        return this.organisationAuthorityService
            .aircraftRegisterExpiries(
                this.authority.id,
                this.register.id,
                this.managingOrganisationId
            )
            .pipe(
                tap(expiry => (this.expiry = expiry)),
                takeUntil(this.ngUnsubscribe$)
            );
    }

    refreshCandidates(): Observable<NameId[]> {
        const doneWorking = this.workTracker.createTracker();

        return this.craftService
            .findCrafts(this.selectedCandidateOrganisationId)
            .pipe(
                map(rpas =>
                    rpas.map(r => ({
                        id: r.id,
                        name: this.formatRpaPipe.transform(r)!
                    }))
                ),
                tap({ complete: doneWorking }),
                takeUntil(this.ngUnsubscribe$)
            );
    }

    onCandidateOrganisationChanged(orgId: number) {
        this.selectedCandidateOrganisationId = orgId;
        this.refreshCandidates()
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: candidates => {
                    this.candidates = candidates;
                    this.add.controls.subjectId.patchValue([]);
                },
                error: error =>
                    this.logging.error(
                        error,
                        'Error refreshing candidates for selected organisation'
                    )
            });
    }

    refreshExisting() {
        return this.rpaRegister.find().pipe(
            mergeMap(candidates =>
                candidates != null && candidates.length > 0
                    ? forkJoin(
                          candidates
                              .filter(c => c.rpaId != null)
                              .map(c =>
                                  this.craftService
                                      .findById(
                                          c.rpaId,
                                          this.managingOrganisationId
                                      )
                                      .pipe(
                                          map(
                                              craft =>
                                                  ({
                                                      ...c,
                                                      rpaDetails: craft
                                                  } as AircraftRegisterEntryWithRpaMaintenance)
                                          ),
                                          catchError(error => {
                                              // Log the error but continue with entry without RPA details
                                              this.logging.error(
                                                  error,
                                                  `Failed to load register details for ${c.rpa?.nickname}`
                                              );
                                              return of({
                                                  ...c,
                                                  rpaDetails: null
                                              } as AircraftRegisterEntryWithRpaMaintenance);
                                          })
                                      )
                              )
                      )
                    : of([])
            ),
            map(candidates =>
                candidates != null
                    ? candidates
                          .filter(c => c.rpaDetails != null)
                          .map(c => this.toRegisterEntry(c))
                    : []
            ),
            takeUntil(this.ngUnsubscribe$)
        );
    }

    refreshChecks() {
        return this.rpaRegister.findRegisterConditions().pipe(
            map(conditions =>
                conditions.conditions
                    .filter(c => c.enabled)
                    .map(c => ({
                        id: c.id,
                        entity: c.entity,
                        entityId: c.entityId,
                        name: c.name,
                        popover: null,
                        conditionType: c.conditionType,
                        description: c.description
                    }))
            )
        );
    }

    findCheckResult(entry: RegisterEntry, check: RegisterCheck) {
        if (this.checkResults == null || this.checkResults[entry.id] == null) {
            return null;
        }

        const results = this.checkResults[entry.id];
        if (results[check.id] != null) {
            return results[check.id];
        } else if (results[check.entityId] != null) {
            return results[check.entityId];
        } else {
            const key = Object.keys(results)
                .filter(k => k != 'status')
                .find(
                    k => results[k]?.checkConditionType === check.conditionType
                );
            if (key != null) {
                return results[key];
            } else {
                return null;
            }
        }
    }

    private toRegisterEntry(
        c: AircraftRegisterEntryWithRpaMaintenance
    ): RpaRegisterEntry {
        return {
            id: c.id,
            startDate: fromLocalDate(c.startDate),
            endDate: fromLocalDate(c.endDate),
            subjectId: c.rpaId,
            subjectName: this.formatRpaPipe.transform(c.rpa),
            statusSource: c.statusSource,
            maintenanceAuthority:
                c.rpaDetails?.assignedMaintenanceSchedule?.authority,
            assignedMaintenanceSchedule:
                c.rpaDetails?.assignedMaintenanceSchedule,
            managingOrganisationId: c.managingOrganisationId
        };
    }

    private parseAuthorities(authorities: OrganisationAuthorityGroup[]) {
        return authorities
            .filter(g => g.hasWorkflow)
            .reduce(
                (acc, g) =>
                    acc.concat(
                        g.authorities.map(
                            a =>
                                ({
                                    id: a.id,
                                    name: `${
                                        g.abbreviation != null
                                            ? g.abbreviation
                                            : g.name
                                    } ${a.identifier ?? ''} ${a.startDate}`
                                } as NameId)
                        )
                    ),
                []
            );
    }

    showEntry(entry: RegisterEntry): void {
        this.organisationService
            .findByIdForUser(this.organisationId, this.organisationId)
            .pipe(
                switchMap(organisation =>
                    this.craftService
                        .findById(entry.subjectId, this.organisationId)
                        .pipe(
                            tap(rpa =>
                                this.rpaDialoguesService.showCraftDetails(
                                    rpa.id,
                                    organisation.id,
                                    false
                                )
                            )
                        )
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe({
                error: error => {
                    this.logging.error(error, 'Error showing entry details');
                }
            });
    }

    createConcreteEntry() {
        if (this.add.invalid) {
            this.add.markAllAsTouched();
            this.logging.error(null, 'Not all fields have been completed');
            return;
        }

        const entry = {
            ...this.add.value,
            statusSource: this.authority.availableActions.canManage
                ? this.add.value.statusSource
                : ('NOMINATED' as CreateAircraftRegisterEntryCommand.StatusSource)
        };

        const selectedRpaIds = Array.isArray(entry.subjectId)
            ? entry.subjectId
            : [entry.subjectId];
        const createObservables = selectedRpaIds.map(rpaId =>
            this.rpaRegister
                .createRegisterEntry({
                    aircraftId: rpaId,
                    startDate: toLocalDate(entry.startDate),
                    endDate: toLocalDate(entry.endDate),
                    statusSource: entry.statusSource,
                    managingOrganisationId: this.organisationId
                })
                .pipe(
                    tap(() =>
                        this.organisationAuthorityService.notifyUpdated(
                            this.authority.id
                        )
                    ),
                    map(c => this.toRegisterEntry(c))
                )
        );
        forkJoin<RpaRegisterEntry[] | null>(
            createObservables.length > 0 ? createObservables : [of(null)]
        )
            .pipe(
                switchMap(results => {
                    const validResults: RegisterEntry[] = results.filter(
                        (r): r is RegisterEntry => r !== null
                    );
                    if (validResults.length > 0) {
                        this.entries.push(...validResults);
                    }
                    this.add.reset();
                    this.add.controls.subjectId.patchValue([]);
                    this.add.controls.statusSource.patchValue('AUTO');
                    this.logging.success(
                        `${validResults.length} new entr${
                            validResults.length === 1 ? 'y' : 'ies'
                        } added to register`
                    );
                    return this.refreshExpiries().pipe(
                        switchMap(() => this.refreshCheckResults())
                    );
                }),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe({
                next: checkResults => {
                    this.checkResults = checkResults;
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while adding RPA to register: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    deleteConcreteEntry(entry: RegisterEntry) {
        const deleteRegisterEntry = () => {
            return firstValueFrom(
                this.rpaRegister
                    .deleteRegisterEntry(entry.id, this.organisationId)
                    .pipe(
                        tap(() =>
                            this.organisationAuthorityService.notifyUpdated(
                                this.authority.id
                            )
                        )
                    )
            ).then(
                () => {
                    const index = this.entries.indexOf(entry);
                    this.entries.splice(index, 1);
                    this.logging.success('Register entry deleted');
                },
                error => this.logging.error(error)
            );
        };
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Delete entry',
                'Are you sure you wish to delete this authority register entry?',
                'Delete',
                deleteRegisterEntry
            )
            .catch(DO_NOTHING);
    }

    updateConcreteEntry() {
        const payload = this.edit.value;

        this.rpaRegister
            .updateRegisterEntry(this.editing, {
                startDate: toLocalDate(payload.startDate),
                endDate: toLocalDate(payload.endDate),
                statusSource: payload.statusSource,
                managingOrganisationId: this.organisationId
            })
            .pipe(
                tap(() =>
                    this.organisationAuthorityService.notifyUpdated(
                        this.authority.id
                    )
                ),
                map(c => this.toRegisterEntry(c)),
                switchMap(result => {
                    const index = this.entries.findIndex(
                        e => e.id === this.editing
                    );
                    this.entries.splice(index, 1, result);
                    this.editing = null;
                    this.logging.success('Register entry updated');
                    return this.refreshExpiries().pipe(
                        switchMap(() => this.refreshCheckResults())
                    );
                })
            )
            .subscribe({
                next: checkResults => {
                    this.checkResults = checkResults;
                },
                error: error => {
                    this.logging.error(error);
                }
            })
            .add(this.workTracker.createTracker());
    }

    updateRpaMaintenanceAuthority(entry: RpaRegisterEntry, value: any) {
        const command = {
            organisationId: this.organisationId,
            maintenanceScheduleId:
                entry.assignedMaintenanceSchedule?.overrideMaintenanceSchedule
                    ?.id ??
                entry.assignedMaintenanceSchedule?.defaultMaintenanceSchedule
                    ?.id,
            authorityId: value.maintenanceAuthorityId
        };
        this.craftService
            .assignMaintenanceSchedule(entry.subjectId, command)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.refreshExistingRpa())
            .add(this.workTracker.createTracker());
    }

    refreshExistingRpa() {
        this.refreshExisting()
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: entries => {
                    this.entries = entries;
                    this.entriesBuf$.next(entries);
                },
                error: error => {
                    this.logging.error(
                        error,
                        'Error refreshing existing RPA entries'
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    onConditionsChanged() {
        this.refreshRegister();
        this.refreshConditions();
    }

    openPopover(pop: any) {
        this.popover = pop;
    }

    /**
     * When the datepicker inside the authority-check popover closes, the
     * click that selected the date is misidentified as an outside click by
     * the popover's outside-click listener (because the datepicker calendar
     * element is removed from the DOM before the click bubbles to document).
     *
     * This fires synchronously before the click event finishes bubbling to the
     * popover's document-level outside-click handler. We temporarily replace
     * popover.hide() with a no-op for one tick so the spurious outside-click
     * does not close the popover, then restore the original method.
     */
    onDatepickerClosed() {
        if (!this.popover?.isOpen) {
            return;
        }
        const originalHide = this.popover.hide.bind(this.popover);
        (this.popover as any).hide = DO_NOTHING;
        setTimeout(() => {
            (this.popover as any).hide = originalHide;
        });
    }

    refreshConditions() {
        this.refreshChecks()
            .pipe(
                switchMap(checks => {
                    this.checks = checks;
                    return this.refreshExpiries().pipe(
                        switchMap(() => this.refreshCheckResults())
                    );
                }),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe({
                next: checkResults => {
                    this.checkResults = checkResults;
                },
                error: error => {
                    this.logging.error(error, 'Error refreshing conditions');
                }
            })
            .add(this.workTracker.createTracker());
    }

    showEditMode(entry: RegisterEntry): void {
        this.editing = entry.id;

        this.edit.setValue({
            startDate: entry.startDate,
            endDate: entry.endDate,
            statusSource: <SelectableStatusSource>entry.statusSource
        });
        this.edit.controls.startDate.setValidators([
            Validators.required,
            validateExistingEntityStartDate(entry.id, this.entries, 'RPA')
        ]);
        this.edit.controls.endDate.setValidators([
            validateExistingEntityEndDate(entry.id, this.entries, 'RPA')
        ]);
        entry.editable = true;
    }

    cancelEditMode(entry: RegisterEntry): void {
        entry.editable = false;
        this.editing = null;
    }

    sort(name: string) {
        if (this.sortList[name]) {
            this.sortList[name] = {
                ascending: !this.sortList[name].ascending
            };
        } else {
            this.sortList = {};
            this.sortList[name] = {
                ascending: true
            };
        }
        this.sortTable(name, this.sortList[name].ascending);
    }

    sortTable(name: string, ascending: boolean) {
        if (ascending) {
            this.entryAsc(name);
        } else {
            this.entryDes(name);
        }
    }

    entryAsc(name: string) {
        this.entries.sort((a, b) => {
            if (a[name] > b[name]) {
                return 1;
            } else if (a[name] < b[name]) {
                return -1;
            } else {
                return 0;
            }
        });
    }

    entryDes(name: string) {
        this.entries.sort((a, b) => {
            if (a[name] > b[name]) {
                return -1;
            } else if (a[name] < b[name]) {
                return 1;
            } else {
                return 0;
            }
        });
    }

    filterByStatus(value: string) {
        if (value === 'all') {
            this.entries = this.entriesBuf$.getValue();
        } else {
            this.entries = this.entriesBuf$.getValue().filter(entry => {
                return this.checkResults[entry.id]?.status === value;
            });
        }
    }

    hasError(controlName: string) {
        const control = this.formGroup.get(controlName);
        return (control.touched || control.dirty) && control.invalid;
    }

    get add() {
        return this.formGroup.controls.add;
    }

    get edit() {
        return this.formGroup.controls.edit;
    }
}
